import { ExternalLinkIcon, TriangleAlertIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import type { PossibleDuplicate } from '@/services/animal-management/api';
import {
  type AnimalRow,
  mapAnimalDto,
} from '@/services/animal-management/index';

type PossibleDuplicatesPanelProps = {
  duplicates: PossibleDuplicate[];
  reason: string;
  onReasonChange: (reason: string) => void;
  onOpenExisting?: (animal: AnimalRow) => void;
  /** Edit and Bulk Add word the warning for saving rather than creating. */
  mode?: 'create' | 'edit';
  reasonId?: string;
};

const MATCH_LABEL_KEYS: Record<string, [key: string, fallback: string]> = {
  metalTag: ['animalManagement.columns.rfidTag', 'RFID Tag'],
  registration: ['animalManagement.registrationNumber', 'Registration Number'],
  tag: ['animalManagement.columns.tag', 'Tag'],
  name: ['animalManagement.duplicates.nameAndBirthDate', 'Name and birth date'],
};

export function PossibleDuplicatesPanel({
  duplicates,
  reason,
  onReasonChange,
  onOpenExisting,
  mode = 'create',
  reasonId = 'add-animal-duplicate-reason',
}: PossibleDuplicatesPanelProps) {
  const { t } = useTranslation();
  const isEdit = mode === 'edit';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
        <TriangleAlertIcon className="mt-0.5 size-5 shrink-0" />
        <div>
          <p className="font-semibold">
            {t(
              'animalManagement.duplicates.title',
              'Possible duplicate animal',
            )}
          </p>
          <p className="text-sm">
            {isEdit
              ? t(
                  'animalManagement.duplicates.editDetail',
                  'Active animals already match your changes. Open the existing animal, or give a reason to save anyway.',
                )
              : t(
                  'animalManagement.duplicates.detail',
                  'Active animals already match what you entered. Open the existing animal, or give a reason to create this one anyway.',
                )}
          </p>
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              {t('animalManagement.columns.animalType', 'Animal Type')}
            </TableHead>
            <TableHead>
              {t('animalManagement.columns.togId', 'TOG ID')}
            </TableHead>
            <TableHead>
              {t('animalManagement.columns.nameTag', 'Name (Tag)')}
            </TableHead>
            <TableHead>
              {t('animalManagement.columns.rfidTag', 'RFID Tag')}
            </TableHead>
            <TableHead>
              {t('animalManagement.registrationNumber', 'Registration Number')}
            </TableHead>
            <TableHead>
              {t('animalManagement.columns.ranch', 'Ranch')}
            </TableHead>
            <TableHead>{t('animalManagement.columns.yard', 'Yard')}</TableHead>
            <TableHead>
              {t('animalManagement.duplicates.matchedOn', 'Matched on')}
            </TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {duplicates.map(({ animal, matchedOn }) => {
            const row = mapAnimalDto(animal);
            return (
              <TableRow key={row.id}>
                <TableCell>{row.animalType}</TableCell>
                <TableCell>{row.togId}</TableCell>
                <TableCell>{row.nameTag}</TableCell>
                <TableCell>{row.rfidTag}</TableCell>
                <TableCell>{row.registration}</TableCell>
                <TableCell>{row.ranch}</TableCell>
                <TableCell>{row.yard}</TableCell>
                <TableCell>
                  {matchedOn
                    .map((field) =>
                      MATCH_LABEL_KEYS[field]
                        ? t(...MATCH_LABEL_KEYS[field])
                        : field,
                    )
                    .join(', ')}
                </TableCell>
                <TableCell className="text-right">
                  {onOpenExisting && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onOpenExisting(row)}
                    >
                      <ExternalLinkIcon className="mr-1.5 size-4" />
                      {t('animalManagement.duplicates.openExisting', 'Open')}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Field>
        <FieldLabel htmlFor={reasonId}>
          {isEdit
            ? t(
                'animalManagement.duplicates.editReasonLabel',
                'Reason for saving anyway',
              )
            : t(
                'animalManagement.duplicates.reasonLabel',
                'Reason for creating anyway',
              )}
          *
        </FieldLabel>
        <Textarea
          id={reasonId}
          value={reason}
          maxLength={500}
          placeholder={t(
            'animalManagement.duplicates.reasonPlaceholder',
            'Why is this a different animal?',
          )}
          onChange={(event) => onReasonChange(event.target.value)}
        />
      </Field>
    </div>
  );
}
